type CalendarColorId = number | string;

export interface CalendarColorOption {
  id: string;
  hex: string;
  accent: string;
  card: string;
  futureCard: string;
}

export const CALENDAR_COLOR_OPTIONS: CalendarColorOption[] = [
  {
    id: 'indigo',
    hex: '#6568ff',
    accent: 'bg-indigo-500',
    card: 'bg-indigo-500/5 hover:bg-indigo-500/10 border-indigo-500/20 hover:border-indigo-500/40',
    futureCard: 'bg-indigo-500/30 hover:bg-indigo-500/50 border-indigo-500/50 hover:border-indigo-500/70 shadow-sm shadow-indigo-500/10',
  },
  {
    id: 'violet',
    hex: '#9364ff',
    accent: 'bg-violet-500',
    card: 'bg-violet-500/5 hover:bg-violet-500/10 border-violet-500/20 hover:border-violet-500/40',
    futureCard: 'bg-violet-500/30 hover:bg-violet-500/50 border-violet-500/50 hover:border-violet-500/70 shadow-sm shadow-violet-500/10',
  },
  {
    id: 'sky',
    hex: '#26baff',
    accent: 'bg-sky-500',
    card: 'bg-sky-500/5 hover:bg-sky-500/10 border-sky-500/20 hover:border-sky-500/40',
    futureCard: 'bg-sky-500/30 hover:bg-sky-500/50 border-sky-500/50 hover:border-sky-500/70 shadow-sm shadow-sky-500/10',
  },
  {
    id: 'emerald',
    hex: '#16f8ac',
    accent: 'bg-emerald-500',
    card: 'bg-emerald-500/5 hover:bg-emerald-500/10 border-emerald-500/20 hover:border-emerald-500/40',
    futureCard: 'bg-emerald-500/30 hover:bg-emerald-500/50 border-emerald-500/50 hover:border-emerald-500/70 shadow-sm shadow-emerald-500/10',
  },
  // {
  //   id: 'coral',
  //   hex: '#F88E87',
  //   accent: 'bg-rose-300',
  //   card: 'bg-rose-300/10 hover:bg-rose-300/20 border-rose-300/30 hover:border-rose-300/50',
  //   futureCard: 'bg-rose-300/40 hover:bg-rose-300/60 border-rose-300/60 hover:border-rose-300/80 shadow-sm shadow-rose-300/20',
  // },
  {
    id: 'lime',
    hex: '#81D038',
    accent: 'bg-lime-500',
    card: 'bg-lime-500/5 hover:bg-lime-500/10 border-lime-500/20 hover:border-lime-500/40',
    futureCard: 'bg-lime-500/30 hover:bg-lime-500/50 border-lime-500/50 hover:border-lime-500/70 shadow-sm shadow-lime-500/10',
  },
  {
    id: 'orange',
    hex: '#FFA000',
    accent: 'bg-orange-500',
    card: 'bg-orange-500/5 hover:bg-orange-500/10 border-orange-500/20 hover:border-orange-500/40',
    futureCard: 'bg-orange-500/30 hover:bg-orange-500/50 border-orange-500/50 hover:border-orange-500/70 shadow-sm shadow-orange-500/10',
  },
  {
    id: 'rose',
    hex: '#ff3d5e',
    accent: 'bg-rose-500',
    card: 'bg-rose-500/5 hover:bg-rose-500/10 border-rose-500/20 hover:border-rose-500/40',
    futureCard: 'bg-rose-500/30 hover:bg-rose-500/50 border-rose-500/50 hover:border-rose-500/70 shadow-sm shadow-rose-500/10',
  },
  {
    id: '#ff6bb5',
    hex: '#ff6bb5',
    accent: 'bg-pink-500',
    card: 'bg-pink-500/5 hover:bg-pink-500/10 border-pink-500/20 hover:border-pink-500/40',
    futureCard: 'bg-pink-500/30 hover:bg-pink-500/50 border-pink-500/50 hover:border-pink-500/70 shadow-sm shadow-pink-500/10',
  },
];

const LOCAL_STORAGE_COLORS_KEY = 'dg-calendar-colors';

export function getStoredCalendarColors(): Record<string, string> {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_COLORS_KEY);
    return data ? JSON.parse(data) : {};
  } catch (e) {
    console.error('Error reading calendar colors from localStorage', e);
    return {};
  }
}

export function saveCalendarColor(calendarId: string | number, color: string): void {
  try {
    const colors = getStoredCalendarColors();
    colors[String(calendarId)] = color;
    localStorage.setItem(LOCAL_STORAGE_COLORS_KEY, JSON.stringify(colors));
  } catch (e) {
    console.error('Error saving calendar color to localStorage', e);
  }
}

export function getCalendarColor(calendarId: string | number): string | undefined {
  const colors = getStoredCalendarColors();
  return colors[String(calendarId)];
}

function getThemeByColorId(colorId: string | undefined): CalendarColorOption {
  if (colorId) {
    const normalized = String(colorId).trim().toLowerCase();
    const found = CALENDAR_COLOR_OPTIONS.find((opt) => (
      opt.id.toLowerCase() === normalized || opt.hex.toLowerCase() === normalized
    ));
    if (found) return found;
  }
  return CALENDAR_COLOR_OPTIONS[0];
}

function getCalendarThemeIndexById(calendarId: CalendarColorId): number {
  const hash = String(calendarId)
    .split('')
    .reduce((total, char) => total + char.charCodeAt(0), 0);

  return hash % CALENDAR_COLOR_OPTIONS.length;
}

/**
 * Returns the Tailwind accent class for a calendar.
 * Prefers explicit color stored in `calendarColor` or localStorage, falls back to hash of id.
 */
function getCalendarAccentClass(
  calendarId: CalendarColorId,
  calendarColor?: string,
): string {
  const color = calendarColor || getCalendarColor(calendarId);
  if (color) {
    return getThemeByColorId(color).accent;
  }
  return CALENDAR_COLOR_OPTIONS[getCalendarThemeIndexById(calendarId)].accent;
}

/**
 * Returns the Tailwind card classes for a calendar event.
 * Prefers explicit color stored in `calendarColor` or localStorage, falls back to hash of id.
 */
function getCalendarEventThemeClass(
  calendarId: CalendarColorId,
  calendarColor?: string,
  isFuture?: boolean,
): string {
  const color = calendarColor || getCalendarColor(calendarId);
  if (color) {
    return isFuture ? getThemeByColorId(color).futureCard : getThemeByColorId(color).card;
  }
  return isFuture
    ? CALENDAR_COLOR_OPTIONS[getCalendarThemeIndexById(calendarId)].futureCard
    : CALENDAR_COLOR_OPTIONS[getCalendarThemeIndexById(calendarId)].card;
}

/**
 * Returns the Hex color value for a calendar.
 * Prefers explicit color stored in `calendarColor` or localStorage, falls back to hash of id.
 */
function getCalendarHexColor(
  calendarId: CalendarColorId,
  calendarColor?: string,
): string {
  const color = calendarColor || getCalendarColor(calendarId);
  if (color) {
    const normalized = String(color).trim().toLowerCase();
    const found = CALENDAR_COLOR_OPTIONS.find((opt) => (
      opt.id.toLowerCase() === normalized || opt.hex.toLowerCase() === normalized
    ));
    if (found) return found.hex;
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(color).trim())) {
      return String(color).trim();
    }
    return getThemeByColorId(color).hex;
  }
  return CALENDAR_COLOR_OPTIONS[getCalendarThemeIndexById(calendarId)].hex;
}

export {
  getCalendarAccentClass,
  getCalendarEventThemeClass,
  getCalendarHexColor,
};
